import { type CSSProperties, type ReactNode } from 'react';

import { labelSpan } from './BarLabelOverlay.sty.js';
import { BAR_LABEL_MIN_SIZE, BAR_LABEL_PADDING } from './label-utils.js';
import type { TruncationMode } from '../../../../../typography/text-ellipsis/TextEllipsis.js';
import { charsFontWidth12pixelsSize400Map } from '../../../../core/components/text-ellipsis/chars-font-width-map.js';
import { truncateString } from '../../../../core/components/text-ellipsis/truncate-string.js';

interface HorizontalBarLabelProps {
  x: number;
  y: number;
  barWidth: number;
  barHeight: number;
  canvasWidth: number;
  isReversed: boolean;
  text: string;
  insideColor: string;
  outsideColor: string;
  truncationMode: TruncationMode;
}

/**
 * HTML label for a single horizontal bar. Colored via a hard-stop gradient
 * clipped to the text rather than two overlapping copies of the text, which
 * used to render visibly bolder on Windows (APPDEV-19526).
 *
 * When `isReversed` is true (RTL), the layout mirrors: bars grow
 * right-to-left and the label anchors at the bar's right edge.
 */
export function HorizontalBarLabel({
  x,
  y,
  barWidth,
  barHeight,
  canvasWidth,
  isReversed,
  text,
  insideColor,
  outsideColor,
  truncationMode,
}: Readonly<HorizontalBarLabelProps>): ReactNode {
  if (barHeight < BAR_LABEL_MIN_SIZE) {
    return null;
  }

  const centerY = y + barHeight / 2;
  const availLength = isReversed
    ? x + barWidth - 2 * BAR_LABEL_PADDING
    : canvasWidth - x - 2 * BAR_LABEL_PADDING;
  if (availLength < BAR_LABEL_MIN_SIZE) {
    return null;
  }

  const truncated = truncateString(
    text,
    availLength,
    charsFontWidth12pixelsSize400Map,
    truncationMode,
  );

  const insideExtent = Math.max(
    0,
    Math.min(availLength, barWidth - BAR_LABEL_PADDING),
  );
  const insidePercent = (insideExtent / availLength) * 100;
  const gradient = isReversed
    ? `linear-gradient(to right, ${outsideColor} 0%, ${outsideColor} ${100 - insidePercent}%, ${insideColor} ${100 - insidePercent}%, ${insideColor} 100%)`
    : `linear-gradient(to right, ${insideColor} 0%, ${insideColor} ${insidePercent}%, ${outsideColor} ${insidePercent}%, ${outsideColor} 100%)`;

  const style: CSSProperties = {
    ...(isReversed
      ? {
          right: canvasWidth - (x + barWidth) + BAR_LABEL_PADDING,
          textAlign: 'right' as const,
        }
      : { left: x + BAR_LABEL_PADDING }),
    top: centerY,
    transform: 'translateY(-50%)',
    width: availLength,
    backgroundImage: gradient,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  };

  return (
    <span className={labelSpan} style={style}>
      {truncated}
    </span>
  );
}

HorizontalBarLabel['displayName'] = 'HorizontalBarLabel';
